'use client'

import { useState, useTransition } from 'react'
import { voteSong } from '@/app/songlist/actions'
import { ThumbsUp, Music2, Youtube } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@/lib/utils'
import AudioPlayer from './AudioPlayer'
import StarRating from './StarRating'

type Category = { id: string, name: string }
type Song = { 
    id: string, 
    title: string, 
    artist: string, 
    language: string, 
    votes: number, 
    categoryId: string, 
    audioUrl?: string | null,
    youtubeUrl?: string | null,
    averageRating?: number,
    totalRatings?: number,
    userRating?: number
}

export default function SongListView({ 
  initialSongs, 
  categories,
  userId
}: { 
  initialSongs: Song[], 
  categories: Category[],
  userId?: string
}) {
  const [activeCategory, setActiveCategory] = useState<string>('all')
  const [activeLanguage, setActiveLanguage] = useState<string>('all')
  const [isPending, startTransition] = useTransition()

  // Get unique languages
  const languages = Array.from(new Set(initialSongs.map(s => s.language).filter(Boolean)))

  // Filter songs
  const filteredSongs = initialSongs.filter(song => {
    const matchCat = activeCategory === 'all' || song.categoryId === activeCategory
    const matchLang = activeLanguage === 'all' || song.language === activeLanguage
    return matchCat && matchLang
  })

  async function handleVote(id: string) {
    // Optimistic Update could go here, but for simplicity relying on revalidate
    startTransition(async () => {
      await voteSong(id)
    })
  }

  return (
    <div className="space-y-8">
      
      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 justify-between items-center bg-black/40 p-4 rounded-xl border border-white/10 backdrop-blur-md">
        
        {/* Categories */}
        <div className="flex gap-2 overflow-x-auto w-full md:w-auto pb-2 md:pb-0 scrollbar-hide">
          <button 
            onClick={() => setActiveCategory('all')}
            className={cn(
               "px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all",
               activeCategory === 'all' 
                 ? "bg-neon-pink text-white shadow-[0_0_15px_rgba(255,0,128,0.5)]" 
                 : "bg-white/5 text-zinc-400 hover:bg-white/10"
            )}
          >
            All Categories
          </button>
          {categories.map(cat => (
             <button 
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={cn(
                 "px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all",
                 activeCategory === cat.id 
                   ? "bg-neon-blue text-white shadow-[0_0_15px_rgba(0,255,255,0.5)]" 
                   : "bg-white/5 text-zinc-400 hover:bg-white/10"
              )}
            >
              {cat.name}
            </button>
          ))}
        </div>

        {/* Languages */}
        <div className="flex gap-2 overflow-x-auto w-full md:w-auto pb-2 md:pb-0 scrollbar-hide">
          <select 
            value={activeLanguage}
            onChange={(e) => setActiveLanguage(e.target.value)}
            className="bg-black/50 border border-white/20 rounded-lg px-4 py-2 text-sm text-white focus:outline-none focus:border-neon-blue"
          >
            <option value="all">All Languages</option>
            {languages.map(lang => (
              <option key={lang} value={lang}>{lang}</option>
            ))}
          </select>
        </div>
      </div>

      {/* List */}
      <div className="grid gap-3">
        <AnimatePresence mode="popLayout">
          {filteredSongs.map((song, index) => (
            <motion.div 
              key={song.id}
              layout
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="group relative overflow-hidden bg-white/5 border border-white/10 rounded-lg hover:bg-white/10 transition-colors"
            >
              {/* Main Row */}
              <div className="flex items-center gap-3 p-3">
                {/* Rank Badge */}
                <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-gradient-to-br from-neon-pink/20 to-neon-purple/20 text-neon-blue font-bold text-xs">
                   {index < 3 ? `#${index + 1}` : <Music2 className="w-3 h-3 opacity-50"/>}
                </div>

                {/* Song Info */}
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-white text-base leading-tight truncate">{song.title}</h3>
                  <p className="text-zinc-500 text-xs truncate">{song.artist} • {song.language}</p>
                </div>

                {/* Quick Actions */}
                <div className="flex items-center gap-2">
                  {song.youtubeUrl && (
                    <a 
                      href={song.youtubeUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="p-2 rounded-full bg-red-500/20 hover:bg-red-500/30 text-red-400 transition-colors"
                      onClick={(e) => e.stopPropagation()}
                      title="Listen on YouTube"
                    >
                      <Youtube className="w-4 h-4" />
                    </a>
                  )}
                  
                  <button 
                    onClick={() => handleVote(song.id)}
                    disabled={isPending}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-white/5 hover:bg-neon-pink/20 hover:text-neon-pink transition-all text-xs"
                    title="Upvote"
                  >
                    <ThumbsUp className={cn("w-3 h-3", isPending && "animate-pulse")} />
                    <span className="font-bold text-white/80">{song.votes}</span>
                  </button>
                </div>

                {/* Star Rating - Right Side */}
                <div className="flex-shrink-0 pl-2 border-l border-white/10">
                  <StarRating 
                    songId={song.id} 
                    averageRating={song.averageRating || 0}
                    totalRatings={song.totalRatings || 0}
                    currentRating={song.userRating}
                    userId={userId}
                    readOnly={!userId}
                  />
                </div>
              </div>

              {/* Audio Player Row - Only if audio exists */}
              {song.audioUrl && (
                <div className="px-3 pb-3 pt-0" onClick={(e) => e.stopPropagation()}>
                  <AudioPlayer src={song.audioUrl} />
                </div>
              )}
              
              {/* Highlight effect for top song */}
              {index === 0 && <div className="absolute inset-0 bg-gradient-to-r from-neon-pink/5 to-transparent pointer-events-none" />}
            </motion.div>
          ))}
        </AnimatePresence>
        
        {filteredSongs.length === 0 && (
          <div className="text-center py-20 text-zinc-500">
            No songs found in this category.
          </div>
        )}
      </div>

    </div>
  )
}
